/**
 * #933 PR 2b — pure helpers for the Settings → Capacity cards.
 *
 * Everything here is the "one phrasing" for a value the cards show in more
 * than one place (Principle 1: no second phrasing to drift): weekday names,
 * the assessment preview line, the first-slot + stagger ⇄ times[] expansion,
 * and the date/money conversions the forms need. No React, no fetch.
 */

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const WEEKDAY_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Monday-first display order — how a desk reads a week. Values stay 0 = Sunday. */
export const WEEKDAYS_MON_FIRST: ReadonlyArray<number> = [1, 2, 3, 4, 5, 6, 0];

export const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export function timeToMinutes(hhmm: string): number | null {
  if (!HHMM_REGEX.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return (h as number) * 60 + (m as number);
}

export function minutesToTime(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * First slot + stagger + slots-per-day → the `times[]` the server stores.
 * Returns null when any input is invalid or the last slot would pass
 * midnight (a schedule that wraps to the next day is a typo, not a plan).
 */
export function expandSlotTimes(
  firstSlot: string,
  staggerMinutes: number,
  slotsPerDay: number,
): string[] | null {
  const start = timeToMinutes(firstSlot);
  if (start === null) return null;
  if (!Number.isInteger(staggerMinutes) || staggerMinutes < 5 || staggerMinutes > 720) return null;
  if (!Number.isInteger(slotsPerDay) || slotsPerDay < 1 || slotsPerDay > 48) return null;
  const out: string[] = [];
  for (let i = 0; i < slotsPerDay; i += 1) {
    const t = start + i * staggerMinutes;
    if (t >= 24 * 60) return null;
    out.push(minutesToTime(t));
  }
  return out;
}

export interface SlotPattern {
  firstSlot: string;
  staggerMinutes: number;
  slotsPerDay: number;
}

/**
 * The inverse of expandSlotTimes: evenly spaced times read back as a
 * pattern; anything else (or an empty list) is null and the form shows the
 * explicit times instead. A single time is a pattern of one slot.
 */
export function inferSlotPattern(times: ReadonlyArray<string>, defaultStagger = 20): SlotPattern | null {
  const sorted = [...times].sort();
  const minutes = sorted.map(timeToMinutes);
  if (sorted.length === 0 || minutes.some((m) => m === null)) return null;
  const first = sorted[0] as string;
  if (sorted.length === 1) return { firstSlot: first, staggerMinutes: defaultStagger, slotsPerDay: 1 };
  const ms = minutes as number[];
  const delta = (ms[1] as number) - (ms[0] as number);
  if (delta < 5) return null;
  for (let i = 2; i < ms.length; i += 1) {
    if ((ms[i] as number) - (ms[i - 1] as number) !== delta) return null;
  }
  return { firstSlot: first, staggerMinutes: delta, slotsPerDay: sorted.length };
}

/** "10:00, 10:20" → ['10:00', '10:20'] (sorted, unique) or null on any bad token. */
export function parseTimesInput(raw: string): string[] | null {
  const tokens = raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  if (tokens.length === 0) return null;
  const normalized = tokens.map((t) => (/^\d:\d\d$/.test(t) ? `0${t}` : t));
  if (normalized.some((t) => !HHMM_REGEX.test(t))) return null;
  return [...new Set(normalized)].sort();
}

/**
 * Weekday set → the words a person would say: "Every day", "Mon–Sat",
 * "Tue, Thu", "Mon–Wed, Sat". Runs of three or more collapse to a range.
 * Read in Monday-first order so "Sat, Sun" reads as a weekend, not a wrap.
 */
export function describeWeekdays(days: ReadonlyArray<number>): string {
  const set = new Set(days);
  if (set.size === 0) return 'No days';
  if (set.size === 7) return 'Every day';
  const ordered = WEEKDAYS_MON_FIRST.filter((d) => set.has(d));
  const positions = ordered.map((d) => WEEKDAYS_MON_FIRST.indexOf(d));
  const parts: string[] = [];
  let runStart = 0;
  for (let i = 1; i <= positions.length; i += 1) {
    const contiguous = i < positions.length && (positions[i] as number) === (positions[i - 1] as number) + 1;
    if (contiguous) continue;
    const from = ordered[runStart] as number;
    const to = ordered[i - 1] as number;
    const len = i - runStart;
    if (len >= 3) parts.push(`${WEEKDAY_SHORT[from]}–${WEEKDAY_SHORT[to]}`);
    else parts.push(ordered.slice(runStart, i).map((d) => WEEKDAY_SHORT[d]).join(', '));
    runStart = i;
  }
  return parts.join(', ');
}

/** The live preview under the assessment form: "Mon–Sat · 10:00, 10:20 · 2 per day". */
export function assessmentPreviewLine(input: {
  days: ReadonlyArray<number>;
  times: ReadonlyArray<string> | null;
  dailyCap: number | null;
  slotCap: number | null;
}): string {
  if (input.days.length === 0) return 'Pick at least one day.';
  if (!input.times || input.times.length === 0) return 'Add at least one time.';
  const parts = [describeWeekdays(input.days), input.times.join(', ')];
  if (input.dailyCap !== null && Number.isFinite(input.dailyCap)) {
    parts.push(`${input.dailyCap} per day`);
    if (input.slotCap !== null && input.slotCap !== input.dailyCap) {
      parts[parts.length - 1] += ` (${input.slotCap} per slot)`;
    }
  }
  return parts.join(' · ');
}

/**
 * A stored calendar date (YYYY-MM-DD) as "Sat, Sep 19" — pinned to UTC so
 * the day never shifts by the viewer's zone (the column is a DATE).
 */
export function formatBlockedDate(iso: string, withYear = false): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(d);
}

/** 0 = Sunday … 6 = Saturday for a YYYY-MM-DD, zone-safe. */
export function weekdayOfIsoDate(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.getUTCDay();
}

export function centsToDollarInput(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}

/** "45" / "45.5" / "$45.50" → 4550; anything else → null. */
export function dollarInputToCents(raw: string): number | null {
  const cleaned = raw.trim().replace(/^\$/, '');
  if (cleaned === '' || !/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Minutes of notice → hours for the form (24 → "24", 90 → "1.5"). */
export function minutesToHoursInput(minutes: number | null): string {
  if (minutes === null) return '';
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(Math.round(hours * 100) / 100);
}

/** Whole or fractional hours → minutes (rounded); blank → null; junk → undefined. */
export function hoursInputToMinutes(raw: string): number | null | undefined {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 60);
}

/** Whole-number field → int in [min, max]; blank → null; junk → undefined. */
export function intInput(raw: string, min: number, max: number): number | null | undefined {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isInteger(n) || n < min || n > max) return undefined;
  return n;
}

export function describeWritten(written: { created: number; updated: number; deleted: number }): string {
  const wrote = written.created + written.updated;
  if (wrote === 0 && written.deleted === 0) return 'Saved. Nothing changed.';
  const parts: string[] = [];
  if (wrote > 0) parts.push(`${wrote} rule${wrote === 1 ? '' : 's'} written`);
  if (written.deleted > 0) parts.push(`${written.deleted} removed`);
  return `Saved. ${parts.join(', ')}. Customers see the new times now.`;
}
